import { INoteAttachment, NoteSyncStatus } from "shared";
import { useState } from "react";
import { Tooltip } from "@mui/material";
import { When } from "react-if";
import { CloudArrowUpIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import { env } from "../../../common/lib/env";
import { classNames } from "../../../common/lib/classNames";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface NoteAttachmentsProps {
  attachments: INoteAttachment[];
}

/** Attachment chips under a note; click downloads via the API */
export function NoteAttachments({ attachments }: NoteAttachmentsProps) {
  const request = useApiRequest();
  const accountingName = useAccountingSystemName();
  const [downloading, setDownloading] = useState<number | null>(null);

  if (!attachments?.length) return null;

  async function download(attachment: INoteAttachment) {
    if (downloading) return;

    setDownloading(attachment.id);

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}note-attachments/${attachment.id}/download`,
      });

      const blobUrl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = blobUrl;
      link.setAttribute(`download`, attachment.originalFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
    catch {
      toast.error(`Could not download attachment.`);
    }
    finally {
      setDownloading(null);
    }
  }

  return (
    <ul className={ `flex flex-wrap gap-2 mt-2` }>
      { attachments.map(attachment => (
        <li
          key={ attachment.id }
          className={ `flex items-center gap-1 border border-gray-200 rounded-md px-2 py-1 text-xs` }
        >
          <button
            type={ `button` }
            onClick={ () => download(attachment) }
            disabled={ downloading === attachment.id }
            className={ `flex items-center gap-1 text-gray-600 hover:text-paidniceGreen-500 disabled:opacity-50` }
          >
            <PaperClipIcon className={ `h-3 w-3` } />
            <span>{ attachment.originalFileName }</span>
            <span className={ `text-gray-400` }>{ formatFileSize(attachment.fileSizeBytes) }</span>
          </button>

          <When condition={ attachment.syncStatus === NoteSyncStatus.NOT_SYNCED || attachment.syncStatus === NoteSyncStatus.DEFFERED }>
            <Tooltip title={ `Attachment not yet sent to ${accountingName}` }>
              <CloudArrowUpIcon className={ classNames(`h-4 w-4 text-orange-400`) } />
            </Tooltip>
          </When>

          <When condition={ attachment.syncStatus === NoteSyncStatus.SYNCED }>
            <Tooltip title={ `Sent to ${accountingName}` }>
              <CloudArrowUpIcon className={ classNames(`h-4 w-4 text-green-400`) } />
            </Tooltip>
          </When>

          <When condition={ attachment.syncStatus === NoteSyncStatus.ERROR }>
            <Tooltip title={ attachment.syncError || `Unknown Sync Error` }>
              <CloudArrowUpIcon className={ classNames(`h-4 w-4 text-red-600`) } />
            </Tooltip>
          </When>
        </li>
      )) }
    </ul>
  );
}
